export type LegalSection = {
  title: string
  paragraphs?: string[]
  bullets?: string[]
}

export type LegalDocument = {
  title: string
  updatedAt: string
  sections: LegalSection[]
}

export const legalLinks = {
  privacy: '/privacy',
  terms: '/terms-and-conditions',
} as const

export const privacyPolicy: LegalDocument = {
  title: 'Privacy Policy',
  updatedAt: 'August 12, 2024',
  sections: [
    {
      title: 'Overview',
      paragraphs: [
        'Thank you for choosing to be part of our community at MR. TURING ("Company," "we," "us," or "our"). If you have questions or concerns about this privacy notice or our practices with regard to your personal information, please contact us at contato@misterturing.com.',
        'This privacy notice describes how we might use your information if you visit our websites or engage with us in other related ways, including sales, marketing, or events.',
        'The purpose of this privacy notice is to explain what information we collect, how we use it, and what rights you have in relation to it. If you do not agree with this notice, please discontinue use of our Services.',
      ],
    },
    {
      title: '1. What information do we collect?',
      paragraphs: [
        'We collect personal information that you voluntarily provide to us when you register, express an interest in obtaining information about us or our products and services, participate in activities on our websites, or contact us.',
        'Personal information provided by you may include email addresses, passwords, and other similar information.',
      ],
    },
    {
      title: '2. How do we use your information?',
      paragraphs: [
        'We use personal information collected via our services for business purposes, including legitimate business interests, performance of a contract, consent-based processing, and compliance with legal obligations.',
      ],
      bullets: [
        'To facilitate account creation and logon.',
        'To post testimonials with consent.',
        'To request feedback about our services.',
        'To enable user-to-user communications where applicable.',
        'To manage user accounts and keep them operational.',
        'To fulfill and manage orders.',
        'To deliver and facilitate requested services.',
        'To respond to inquiries and provide support.',
      ],
    },
    {
      title: '3. Will your information be shared with anyone?',
      paragraphs: [
        'We may process or share data based on consent, legitimate interests, performance of a contract, legal obligations, or vital interests.',
        'We may disclose information where required by law, court order, legal process, or to investigate and prevent suspected fraud, policy violations, threats to safety, illegal activities, or litigation risk.',
      ],
    },
    {
      title: '4. How long do we keep your information?',
      paragraphs: [
        'We keep personal information only as long as necessary for the purposes described in this notice unless a longer retention period is required or permitted by law.',
        'When we have no ongoing legitimate business need to process your personal information, we will delete or anonymize it. If deletion is not immediately possible, we will securely store and isolate it from further processing until deletion is possible.',
      ],
    },
    {
      title: '5. How do we keep your information safe?',
      paragraphs: [
        'We implement technical and organizational security measures designed to protect personal information we process.',
        'No electronic transmission or storage technology can be guaranteed to be fully secure. You should access the services only within a secure environment.',
      ],
    },
    {
      title: '6. Do we collect information from minors?',
      paragraphs: [
        'We do not knowingly solicit data from or market to children under 18 years of age.',
        'If we learn that personal information from users under 18 has been collected, we will deactivate the account and take reasonable measures to delete that data. If you become aware of such data, contact us at contato@misterturing.com.',
      ],
    },
    {
      title: "7. How do we use Google's data?",
      paragraphs: [
        'We use Google data during signup and to access Google Calendar meetings when users authorize that access.',
        "Mr. Turing's use and transfer of information received from Google APIs will adhere to the Google API Services User Data Policy, including Limited Use requirements.",
      ],
    },
    {
      title: '8. What are your privacy rights?',
      paragraphs: [
        'You may review, change, or terminate your account at any time.',
        'If you have questions or comments about your privacy rights, or would like to review, change, or terminate your account, email contato@misterturing.com.',
        'Upon request to terminate an account, we will deactivate or delete the account and information from active databases, subject to retention needed to prevent fraud, troubleshoot issues, assist investigations, enforce Terms of Use, or comply with legal requirements.',
      ],
    },
    {
      title: '9. How can you contact us?',
      paragraphs: [
        'If you have questions or comments about this notice, email contato@misterturing.com.',
      ],
    },
    {
      title: '10. How do we use AI models?',
      paragraphs: [
        'We use AI models to enhance our services and provide better user experiences.',
        'We may use models developed by OpenAI, Google AI models, and in-house open-source models.',
        'Data shared with AI models may include user-generated content and other data you provide. The purpose is to generate insights, summaries, tasks, and other outputs that support Mr. Turing products and services.',
        'Users control what data is shared by choosing what data to upload or make available to the services. We aim to minimize data shared with AI models and apply security measures and ethical guidelines for responsible use.',
      ],
    },
  ],
}

export const termsAndConditions: LegalDocument = {
  title: 'Terms and Conditions',
  updatedAt: 'November 22, 2022',
  sections: [
    {
      title: '1. Agreement to terms',
      paragraphs: [
        'These Terms of Use constitute a legally binding agreement between you, whether personally or on behalf of an entity, and Mr. Turing ("Company," "we," "us," or "our"), concerning your access to and use of our websites, media forms, media channels, mobile websites, mobile applications, and related services.',
        'By accessing the services, you confirm that you have read, understood, and agreed to be bound by these Terms. If you do not agree with all of these Terms, you are prohibited from using the services and must discontinue use immediately.',
        'We may change or modify these Terms from time to time by updating the Last updated date. Continued use after revised Terms are posted means you accept the revised Terms.',
        'The services are intended for users who are at least 18 years old. Persons under 18 are not permitted to use or register for the services.',
      ],
    },
    {
      title: '2. Intellectual property rights and user representations',
      paragraphs: [
        'Unless otherwise indicated, the services, source code, databases, functionality, software, website designs, audio, video, text, photographs, graphics, trademarks, service marks, and logos are owned or controlled by us or licensed to us.',
        'The content and marks are provided for information and personal use only. Except as expressly permitted, no part of the services may be copied, reproduced, aggregated, republished, uploaded, posted, publicly displayed, encoded, translated, transmitted, distributed, sold, licensed, or otherwise exploited for a commercial purpose without our written permission.',
        'You are responsible for keeping your password confidential and for all use of your account and password.',
      ],
      bullets: [
        'You will provide true, accurate, current, and complete registration information.',
        'You have legal capacity and will comply with these Terms.',
        'You will not access the services through automated or non-human means except as permitted.',
        'You will not use the services for an illegal or unauthorized purpose.',
        'Your use of the services will not violate applicable law or regulation.',
      ],
    },
    {
      title: '3. Prohibited activities',
      paragraphs: [
        'You may not access or use the services for any purpose other than those for which we make them available.',
      ],
      bullets: [
        'Systematically retrieve data or content to create or compile a collection, compilation, database, or directory without written permission.',
        'Trick, defraud, mislead, disparage, tarnish, harass, abuse, or harm us or other users.',
        'Circumvent, disable, or interfere with security-related features.',
        'Use automated scripts, data mining, robots, scrapers, or similar data gathering tools except as permitted by standard search engine or browser usage.',
        'Attempt to impersonate another user or person.',
        'Upload or transmit passive or active information collection mechanisms, spyware, or similar devices.',
        'Interfere with, disrupt, or create an undue burden on the services or connected networks.',
        'Reverse engineer, decompile, disassemble, or attempt to derive source code except where permitted by law.',
        'Use the services as part of an effort to compete with us or for an unauthorized commercial enterprise.',
      ],
    },
    {
      title: '4. Third-party websites and content',
      paragraphs: [
        'The services may contain links to third-party websites or content. We do not investigate, monitor, or check third-party websites or content for accuracy, appropriateness, or completeness.',
        'We are not responsible for third-party websites, third-party content, privacy practices, purchases, or transactions. If you leave our services to access third-party websites, you do so at your own risk and should review the applicable terms and policies.',
      ],
    },
    {
      title: '5. Privacy policy and account termination',
      paragraphs: [
        'We care about data privacy and security. By using the services, you agree to be bound by our Privacy Policy, which is incorporated into these Terms.',
        'These Terms remain in full force while you use the services. We may deny access, terminate use, delete accounts, or remove content at any time where permitted by these Terms or applicable law.',
      ],
    },
    {
      title: '6. Modifications and interruptions',
      paragraphs: [
        'We reserve the right to change, modify, or remove service contents at any time or for any reason at our discretion without notice.',
        'We do not guarantee that the services will be available at all times. We may experience hardware, software, or other problems, perform maintenance, or discontinue all or part of the services.',
      ],
    },
    {
      title: '7. Governing law and dispute resolution',
      paragraphs: [
        'These Terms are governed by the laws identified in the applicable agreement or the jurisdiction stated in the then-current published Terms.',
        'Disputes arising out of or in connection with these Terms will be resolved according to the dispute resolution provisions in the applicable agreement or published Terms.',
      ],
    },
    {
      title: '8. Disclaimer and limitation of liability',
      paragraphs: [
        'The services are provided on an as-is and as-available basis. Your use of the services is at your sole risk.',
        'To the fullest extent permitted by law, we disclaim warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement.',
        'We are not liable for indirect, consequential, exemplary, incidental, special, or punitive damages, including lost profit, lost revenue, or loss of data arising from use of the services.',
      ],
    },
    {
      title: '9. Electronic communications, transactions, and signatures',
      paragraphs: [
        'Visiting the services, sending emails, and completing online forms constitute electronic communications. You consent to receive electronic communications, and agree that electronic notices, disclosures, and communications satisfy legal requirements that such communication be in writing.',
      ],
    },
    {
      title: '10. Contact',
      paragraphs: [
        'To resolve a complaint or receive further information regarding use of the services, contact MR. TURING at contato@misterturing.com.',
      ],
    },
  ],
}
